import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, Card, Spinner } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { PeopleAPI, fullName, type Person } from '@/api/people'
import { EnrichmentAPI, TERMINAL_STATUSES } from '@/api/enrichment'
import { ENRICHMENT_VARIANT, confidenceTier, scoreTier } from '@/lib/badges'

type Msg = { kind: 'success' | 'error' | 'info'; text: string }

const BANNER_BG: Record<Msg['kind'], string> = {
  success: '#dcfce7',
  error: '#fee2e2',
  info: '#e5e7eb',
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <div className="dw-field-label">{label}</div>
      <div className="dw-field-value">{value || '—'}</div>
    </>
  )
}

export function PersonDetail() {
  const { id } = useParams<{ id: string }>()
  const { diwaUrl } = useUI()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const api = new PeopleAPI(diwaUrl)
  const enrichApi = new EnrichmentAPI(diwaUrl)

  const [enriching, setEnriching] = useState(false)
  const [msg, setMsg] = useState<Msg | null>(null)

  const { data: person, isLoading, isError, error } = useQuery({
    queryKey: ['person', id],
    queryFn: () => api.get(id!),
    enabled: !!id,
    refetchInterval: enriching ? 2000 : false, // poll while an enrichment is in flight
  })

  const enrichMut = useMutation({
    mutationFn: () => enrichApi.enrich(id!),
    onSuccess: () => {
      // optimistically flip to pending so the poll starts cleanly (avoids an instant "done")
      queryClient.setQueryData<Person>(['person', id], (old) =>
        old ? { ...old, enrichment_status: 'pending' } : old,
      )
      setEnriching(true)
    },
    onError: (e) =>
      setMsg({ kind: 'error', text: `Couldn't queue enrichment: ${(e as Error).message}. Is the worker / Redis running?` }),
  })

  // stop polling when the run reaches a terminal status
  useEffect(() => {
    if (!enriching || !person) return
    if (TERMINAL_STATUSES.has(person.enrichment_status)) {
      setEnriching(false)
      const ok = person.enrichment_status === 'done' || person.enrichment_status === 'manual'
      const conf = person.enrichment_confidence != null
        ? ` · confidence ${Math.round(person.enrichment_confidence * 100)}%`
        : ''
      setMsg({ kind: ok ? 'success' : 'info', text: `Enrichment ${person.enrichment_status}${conf}` })
    }
  }, [enriching, person?.enrichment_status, person?.enrichment_confidence])

  // safety cap: if no worker picks it up, stop after 60s
  useEffect(() => {
    if (!enriching) return
    const t = setTimeout(() => {
      setEnriching(false)
      setMsg({ kind: 'info', text: 'Still processing — the worker may be busy or not running. Check back shortly.' })
    }, 60000)
    return () => clearTimeout(t)
  }, [enriching])

  const inFlight = enriching || person?.enrichment_status === 'pending'
  const enrichLabel = inFlight
    ? 'Enriching…'
    : person && person.enrichment_status !== 'none'
      ? 'Re-enrich'
      : 'Enrich'

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">{person ? fullName(person) : 'Person'}</div>
          {person && (
            <div className="dw-page-subtitle">
              <Badge variant={scoreTier(person.relationship_score).variant}>
                Score {person.relationship_score.toFixed(0)} · {scoreTier(person.relationship_score).label}
              </Badge>{' '}
              <Badge variant={confidenceTier(person.enrichment_confidence).variant}>
                Confidence {confidenceTier(person.enrichment_confidence).label}
              </Badge>{' '}
              <Badge variant={ENRICHMENT_VARIANT[person.enrichment_status] ?? 'neutral'}>
                {person.enrichment_status}
              </Badge>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {person && (
            <Button
              variant="primary"
              size="sm"
              disabled={inFlight || enrichMut.isPending}
              onClick={() => { setMsg(null); enrichMut.mutate() }}
            >
              {inFlight ? <Spinner size="sm" /> : null} {enrichLabel}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/people')}>← Back</Button>
        </div>
      </div>

      {msg && (
        <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8, fontSize: 'var(--ae-font-size-sm)', background: BANNER_BG[msg.kind], color: '#111' }}>
          {msg.text}
        </div>
      )}

      {isLoading && <p className="dw-empty">Loading…</p>}
      {isError && <p className="dw-error">Couldn’t load person: {(error as Error).message}</p>}

      {person && (
        <Card>
          <div className="dw-field-grid">
            <Field label="Email" value={person.email} />
            <Field label="Phone" value={person.phone} />
            <Field label="Title" value={person.title} />
            <Field label="Company" value={person.company_name} />
            <Field label="LinkedIn" value={person.linkedin_url} />
            <Field label="Notes" value={person.notes} />
          </div>
        </Card>
      )}
    </div>
  )
}

export type { Person }
